import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonIcon,
  Modal,
  ModalProps,
  Toggle,
  TrashIcon,
} from '@rabbit/elements/shared-components';
import {
  nestApiCreateTenantMakes,
  nestApiUpdateTenantMakes,
  nestApiDeleteTenantMakes,
} from '@rabbit/bizproc/core';
import { TenantMakesModels } from '@rabbit/data/types';
import { Input } from '@rabbit/elements/shared-components';
import { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { TenantMakesCreateDto, TenantMakesUpdateDto } from '@rabbit/nest/api';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { toast } from 'react-toastify';

interface MakeListShape {
  docid: string;
  id: string;
  value: string;
  label: string;
  name: string;
  enabled: boolean;
  description: string;
}

export default function MakeTab() {
  const { t } = useTranslation();
  const tenantLink = t('tenantLink');
  const { activeTenantInfo } = useContext(UserContext) || {};
  const formRef = useRef(null) as any;
  const [isLoading, setIsLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMake, setSelectedMake] = useState<TenantMakesModels | null>(
    null
  );
  const [makeList, setMakeList] = useState<TenantMakesModels[]>([]);

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        setMakeList(activeTenantInfo?.makes || []);
      } catch (error) {
        console.error('Failed to fetch makes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMakes();
  }, [t]);

  const modalSettings: ModalProps = {
    kind: 'generic' as const,
    settings: {
      title: selectedMake ? t('general.editMake') : t('general.addMake'),
      text: selectedMake ? t('general.editMake') : t('general.addMake'),
      handleClose: () => {
        setEditModalOpen(false);
        setSelectedMake(null);
      },
    },
  };

  const deleteModalSettings: ModalProps = {
    kind: 'generic' as const,
    settings: {
      title: t('Delete Make'),
      text: t('Are you sure you want to delete this make?'),
      primaryButtonText: t('general.delete'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => {
        setDeleteModalOpen(false);
        setSelectedMake(null);
      },
      handleOutlineClick: () => {
        setDeleteModalOpen(false);
        setSelectedMake(null);
      },
      handlePrimaryClick: async () => {
        if (selectedMake) {
          await handleDelete(selectedMake.docid);
          setDeleteModalOpen(false);
          setSelectedMake(null);
        }
      },
    },
  };

  const schema = Yup.object({
    label: Yup.string().required(t('message.pleaseEnterAName')),
    description: Yup.string().optional(),
    enabled: Yup.boolean().optional().default(true),
  });

  const initialValues = {
    label: selectedMake?.label || '',
    description: selectedMake?.description || '',
    enabled: selectedMake?.enabled || true,
  };

  const handleSubmit = async (values: {
    label: string;
    description: string;
    enabled: boolean;
  }) => {
    try {
      if (selectedMake) {
        await handleUpdate(selectedMake.docid, {
          tenantLink,
          make: {
            docid: selectedMake.docid,
            label: values.label,
            description: values.description,
            enabled: values.enabled,
          },
        });
      } else {
        await handleCreate({
          tenantLink,
          makes: {
            docid: `make_${Date.now()}`, // Generate a unique docid
            label: values.label,
            description: values.description,
            enabled: values.enabled,
          },
        });
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleEditModal = (itemDetails?: MakeListShape) => {
    if (itemDetails) {
      setSelectedMake(
        makeList.find((m) => m.docid === itemDetails.docid) || null
      );
    } else {
      setSelectedMake(null);
    }
    setEditModalOpen(true);
  };

  const handleCreate = async (data: TenantMakesCreateDto) => {
    try {
      const response: any = await nestApiCreateTenantMakes(data);
      if (response && response.makes) {
        setMakeList(response.makes);
      }
      setEditModalOpen(false);
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateMakes'));
      } else {
        console.error('Failed to create make:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const handleUpdate = async (docid: string, data: TenantMakesUpdateDto) => {
    try {
      const response: any = await nestApiUpdateTenantMakes(data);
      if (response && response.makes) {
        setMakeList(response.makes);
      }
      setEditModalOpen(false);
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateMakes'));
      } else {
        console.error('Failed to update make:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const handleDelete = async (docid: string) => {
    try {
      const response: any = await nestApiDeleteTenantMakes({
        tenantLink,
        docid,
      });
      if (response && response.makes) {
        toast.success(t('general.makeDeletedSuccessfully'));
        setMakeList(response.makes);
      }
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateMakes'));
      } else {
        console.error('Failed to delete make:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const MakeTable = useMemo(() => {
    const makeListOptions: MakeListShape[] =
      makeList?.map((make) => ({
        docid: make.docid,
        id: make.docid,
        value: make.docid,
        label: make.label,
        name: make.label,
        enabled: make.enabled,
        description: make.description || '-',
      })) || [];

    return CreateTable<MakeListShape>(makeListOptions, [
      {
        header: t('general.name'),
        accessorKey: 'label',
        value: (make) => make.label,
      },
      {
        header: t('general.description'),
        accessorKey: 'description',
        value: (make) => make.description,
      },
      {
        header: t('general.enabled'),
        accessorKey: 'enabled',
        value: (make) => (make.enabled ? t('general.yes') : t('general.no')),
      },
      {
        header: '',
        accessorKey: 'actions',
        value: (make) => (
          <div className="space-x-2 text-center">
            <ButtonIcon
              label={t('general.editMake')}
              kind={'bgLightGreen'}
              iconLeft={true}
              Icon={PencilSquareIcon}
              onClick={() => {
                setSelectedMake(
                  makeList.find((m) => m.docid === make.docid) || null
                );
                handleEditModal(make);
              }}
            />
            <ButtonIcon
              label={t('general.deleteMake')}
              kind={'bgRed'}
              iconLeft={true}
              Icon={TrashIcon}
              onClick={() => {
                setSelectedMake(
                  makeList.find((m) => m.docid === make.docid) || null
                );
                setDeleteModalOpen(true);
              }}
            />
          </div>
        ),
        size: 15,
        maxSize: 30,
      },
    ]);
  }, [makeList, isLoading, t]);

  return (
    <>
      <div className="relative z-10 flex justify-end py-4">
        <div className="md:absolute md:right-5 md:top-[76px] xl:top-[60px]">
          <ButtonIcon
            label={t('general.addMake')}
            iconLeft={true}
            onClick={() => handleEditModal()}
          />
        </div>
      </div>
      <MakeTable
        initialState={{
          columnPinning: { right: ['actions'] },
          showGlobalFilter: true,
          isLoading,
        }}
        muiSearchTextFieldProps={{
          placeholder: t('general.searchNameOrDescription'),
          sx: {
            minWidth: '600px',
            borderRadius: '20px',
            backgroundColor: '#fff',
            margin: '5px 0',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': { borderRadius: '10px' },
            },
          },
          variant: 'outlined',
          margin: 'none',
        }}
      />
      {editModalOpen && (
        <Modal
          className="md:w-[500px] lg:w-[724px]"
          kind={'generic'}
          settings={modalSettings.settings}
        >
          <Formik
            onSubmit={handleSubmit}
            initialValues={selectedMake || initialValues}
            validationSchema={schema}
            validateOnChange={true}
            validateOnBlur={false}
            innerRef={formRef}
          >
            {(props) => (
              <Form className="mt-2 w-full px-5">
                <div className="w-full py-2">
                  <Input
                    type="text"
                    label={`${t('general.name')}*`}
                    name="label"
                    settings={{
                      id: 'label',
                      hint: '*required',
                      placeholder: t('general.makeName'),
                    }}
                  />
                </div>
                <div className="w-full py-2">
                  <Input
                    type="textarea"
                    label={`${t('general.description')}`}
                    name="description"
                    settings={{
                      id: 'description',
                      placeholder: t('general.description'),
                    }}
                  />
                </div>

                <div className="mt-5 flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                  <div className="grow capitalize">{t('general.enabled')}</div>
                  <Toggle
                    name="enabled"
                    checked={props.values.enabled}
                    onChange={() => {
                      props.setFieldValue('enabled', !props.values.enabled);
                    }}
                  />
                </div>

                <div className="mt-8 flex gap-5 py-2">
                  <Button
                    kind={'primary'}
                    type="submit"
                    disabled={props.isSubmitting || !props.isValid}
                  >
                    {t('general.save')}
                  </Button>
                  <Button kind={'red'} onClick={() => setEditModalOpen(false)}>
                    {t('general.cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          className="h-fit min-w-[350px] max-w-[500px] rounded-md bg-white"
          kind={'pop-up'}
          settings={deleteModalSettings.settings}
        />
      )}
    </>
  );
}
